import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, EmptyState, ErrorState, Spinner, Table, Td, Th, cx } from '@/components/ui';
import { changeColorClass, formatDateTime, formatKrw, formatPercent, formatSignedNumber } from '@/lib/format';
import { priorRoundsProfit } from './accountStats';
import {
  AI_REVIEW_MARK,
  AI_REVIEW_MARK_LABEL,
  AI_REVIEW_SCROLL_STATE,
  aiReviewChipText,
  aiReviewHref,
  isAiReviewAccount,
  openEpisodesBySymbol,
} from './aiReviewMark';
import { bandPriceFormat } from './bandCurrency';
import { StockLink } from './StockLink';
import { useSwingStatus } from './usePaper';
import { AI_REVIEW_DAYS, useAiReview } from './useAiReview';
import { AI_REVIEW_OFF_TEXT } from './aiReviewDisplay';
import {
  ALL_SECTORS,
  filterBySector,
  sectorHoldingLabel,
  skipReasonLabel,
  sortByBuyOrder,
  sortByRank,
  swingBuyState,
  swingReasonLabel,
  type SwingBuyState,
} from './swingDisplay';
import type { SwingAccountKey, SwingConfig, SwingPosition, SwingStatus, SwingTarget } from './swingTypes';

/**
 * 국내 스윙 계좌 탭. (2026-10-10 · docs/impl/141 12.6-5 ⑤)
 *
 * 순서 — 계좌 카드 → 보유 → 청산 → 회고 → 감시 대상(맨 아래 · 저점매수 화면과 같이 목록이 길어도 다른 것을 가리지 않게)
 * 🔴 숫자는 서버 응답에서만 — 진입선(재진입 반영) · 못 산 이유 · 유예 · 만료 · 다음 단은 엔진과 같은 값(SwingStatusService)
 * 🔴 실제 주문은 없다 — 가상 계좌다
 * 🇺🇸 미국 스윙(SWING_US_A · 미국 화면)도 이 패널을 쓴다 — 계좌 카드는 원화, 가격 칸은 config.currency(USD)로 가른다(BandPanel 과 같은 bandPriceFormat)
 */
export function SwingPanel({ account }: { account: SwingAccountKey }) {
  const { data, isLoading, error, refetch } = useSwingStatus(account);

  if (isLoading) {
    return <Spinner />;
  }
  if (error || !data) {
    return <ErrorState message={error?.message ?? '스윙 계좌를 불러오지 못했습니다.'} onRetry={() => refetch()} />;
  }
  if (!data.ready || data.account === null) {
    return <EmptyState title="스윙 계좌가 아직 없습니다" description="계좌 행이 만들어지기 전입니다(배포 직후)." />;
  }

  return (
    <div className="space-y-4">
      {!data.active && (
        <p className="rounded-lg bg-surface-hover px-3 py-2 text-sm text-fg-muted">
          {data.config.fromRound !== null ? `${data.config.fromRound}회차` : '다음 회차'}가 열리면 사기 시작합니다. 지금은 감시만 합니다.
        </p>
      )}
      {isAiReviewAccount(account) && <AiReviewNotice />}
      <AccountCard status={data} />
      <PositionsCard positions={data.positions} config={data.config} />
      <ClosedCard status={data} />
      <ReviewsCard status={data} />
      <TargetsCard status={data} account={account} />
    </div>
  );
}

/**
 * 🔎 AI 2차 검토 대상 안내 — 스윙(6개월)만 · 위 "회차가 열리면" 안내와 같은 꼴(2026-10-18 사용자 결정)
 * AI 호출이 꺼졌다고 서버가 알려 주면 "보고서를 씁니다" 대신 꺼짐 문장 — 관리자가 화면에서 끌 수 있게 됐다(2026-10-19 · V158).
 * 설정을 아직 못 받았으면(undefined) 켜진 때 문장 그대로(저점 신호 검토 화면과 같은 규칙 · 같은 조회 키라 요청은 한 번)
 */
function AiReviewNotice() {
  const aiOff = useAiReview(AI_REVIEW_DAYS).data?.config?.llmEnabled === false;
  return (
    <p className="rounded-lg bg-surface-hover px-3 py-2 text-sm text-fg-muted">
      {AI_REVIEW_MARK} AI 2차 검토 대상 계좌 — 이 계좌의 감시 종목이 1단선에 닿으면{' '}
      {/* 2026-10-20 화면 점검 — 새 판(air-v4)은 "왜" 에 답하지 않는다(인과 단정 금지) · 보고서 모양대로 */}
      {aiOff ? `기록을 남깁니다. ${AI_REVIEW_OFF_TEXT}` : "AI가 '지금 위치 · 회사 이야기 · 먼저 확인할 것' 세 줄 요지와 회사 위험을 정리한 보고서를 씁니다."}
      {' '}계좌 매매와는 무관합니다.{' '}
      <Link
        to={aiReviewHref()}
        state={AI_REVIEW_SCROLL_STATE}
        className="whitespace-nowrap text-link underline underline-offset-2"
      >
        보고서 보기 →
      </Link>
    </p>
  );
}

/** 부호 붙은 원 — 값이 없으면 "-" 만(formatSignedNumber 는 단위를 붙이지 않는다) */
function signedWon(value: number | null): string {
  return value === null ? '-' : `${formatSignedNumber(value)}원`;
}

function AccountCard({ status }: { status: SwingStatus }) {
  // 근거: SwingPanel 이 account === null 이면 이 카드를 그리지 않는다
  const a = status.account as NonNullable<SwingStatus['account']>;
  const usd = status.config.currency === 'USD';
  // 원금과 이번 회차 시작이 원 단위로 같으면 "이번 회차" 수익률을 따로 적지 않는다 — 같은 숫자 두 번(AccountStatsCard 와 같은 규칙)
  const sameStart = priorRoundsProfit({ seed: a.base, principal: a.principal }) === null;
  const rows: [string, string][] = [
    ['총자산', formatKrw(a.equity)],
    ['예수금', formatKrw(a.cash)],
    ['추가 매수 몫(예약)', formatKrw(a.reserved)],
    ['원금', formatKrw(a.principal)],
    ['이번 회차 시작', formatKrw(a.base)],
    // 🇺🇸 미국은 원화 손익(환율 몫 포함) — 종목별 손익은 아래 청산 표에 달러로
    [usd ? '실현 손익(원화)' : '실현 손익', signedWon(a.realizedPnl)],
    ['청산 · 승률', `${a.closedCount}건 · ${a.winRate === null ? '-' : `${a.winRate}%`}`],
    ['최대 하락폭', a.maxDrawdownPercent === null ? '-' : formatPercent(a.maxDrawdownPercent)],
  ];
  return (
    <Card title={`🔁 ${a.label}`}>
      {/* 🔴 누적 크게 · 회차 작게 (2026-10-19 사용자 피드백) — 큰 숫자는 원금 대비 누적 수익률이다 */}
      <p className={cx('text-2xl font-semibold', changeColorClass(a.cumulativeReturnPercent))}>
        {formatPercent(a.cumulativeReturnPercent)}
      </p>
      <p className="text-xs tabular text-fg-subtle">
        누적 <span className={changeColorClass(a.cumulativeProfit)}>{signedWon(a.cumulativeProfit)}</span>
        {!sameStart && (
          <>
            {' · '}이번 회차{' '}
            <span className={changeColorClass(a.returnPercent)}>{formatPercent(a.returnPercent)}</span>
          </>
        )}
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-2">
            <dt className="text-fg-muted">{label}</dt>
            <dd className="text-right font-medium text-fg">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-2 text-xs text-fg-subtle">
        큰 숫자는 원금 대비 누적 수익률이고, 이번 회차 수익률은 이번 회차 시작 평가액 대비입니다. 예약은 들고 있는 종목이 더 떨어질 때 추가로 살 돈이라 새 종목을 살 때 쓰지 않습니다.
        {usd && ' 총자산 · 예약은 보유(달러)를 지금 환율로 원화로 바꾼 값입니다.'}
        {usd && a.equity === null && ' 지금 환율을 받지 못해 총자산을 내지 못했습니다.'}
      </p>
    </Card>
  );
}

function PositionsCard({ positions, config }: { positions: SwingPosition[]; config: SwingConfig }) {
  const { px } = bandPriceFormat(config.currency);
  const country = config.currency === 'USD' ? 'US' : 'KR';
  if (positions.length === 0) {
    return (
      <Card title="보유">
        <p className="text-sm text-fg-muted">들고 있는 종목이 없습니다.</p>
      </Card>
    );
  }
  return (
    <Card title={`보유 ${positions.length}종목 (최대 ${config.maxPositions})`}>
      <Table>
        <thead>
          <tr>
            <Th>종목</Th>
            <Th align="right">평단 · 현재가</Th>
            <Th align="right">손절선 · 다음</Th>
          </tr>
        </thead>
        <tbody>
          {positions.map((p) => (
            <tr key={p.id}>
              <Td>
                <StockLink stockId={p.stockId} country={country} className="font-medium text-fg">
                  {p.stockName}
                </StockLink>
                <div className="text-xs text-fg-subtle">
                  {p.sector} · {p.rungs}단 · {p.quantity}주
                </div>
                {/* 표식은 한 줄에 하나 — 옆으로 늘어놓으면 폰에서 종목 칸이 좁아진다(2026-10-19 사용자 · 높이는 늘어도 된다) */}
                <div className="mt-0.5 flex flex-col items-start gap-1">
                  {p.graceDaysLeft > 0 && <Badge tone="info">유예 {p.graceDaysLeft}일 남음</Badge>}
                  <Badge tone={p.expiryDaysLeft <= 5 ? 'warning' : 'neutral'}>만료까지 {p.expiryDaysLeft}거래일</Badge>
                  {p.legsDone > 0 && <Badge tone="success">익절 {p.legsDone}단 지남</Badge>}
                </div>
              </Td>
              <Td align="right" numeric>
                <div>{px(p.avgPrice)}</div>
                <div className={changeColorClass(p.unrealizedPercent)}>
                  {p.currentPrice === null ? '-' : px(p.currentPrice)}
                  {p.unrealizedPercent !== null && ` (${formatPercent(p.unrealizedPercent)})`}
                </div>
              </Td>
              <Td align="right" numeric>
                <div>
                  {px(p.stopPrice)}
                  {p.breakeven && <span className="ml-1 text-xs text-fg-subtle">본전</span>}
                </div>
                <div className="text-xs text-fg-subtle">
                  {p.nextLadderPrice !== null && `익절 ${px(p.nextLadderPrice)}`}
                  {p.nextLegPrice !== null && ` · 추가 ${px(p.nextLegPrice)}`}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <p className="mt-2 text-xs text-fg-subtle">
        유예 기간에는 손절선 아래여도 팔지 않습니다. 익절은 하루에 한 단씩, 만료일에는 {config.expireAfter} 뒤에 정리합니다.
      </p>
    </Card>
  );
}

function ClosedCard({ status }: { status: SwingStatus }) {
  // 🇺🇸 미국은 달러 손익(규칙 성적) — 원화 손익 합은 계좌 카드 "실현 손익(원화)"
  const { usd, signedPx } = bandPriceFormat(status.config.currency);
  if (status.closed.length === 0) {
    return null;
  }
  return (
    <Card title={`이번 회차 청산 ${status.closed.length}건`}>
      <Table>
        <thead>
          <tr>
            <Th>종목</Th>
            <Th align="right">손익</Th>
          </tr>
        </thead>
        <tbody>
          {status.closed.map((c) => (
            <tr key={c.id}>
              <Td>
                <div className="font-medium text-fg">{c.stockName}</div>
                <div className="text-xs text-fg-subtle">
                  {swingReasonLabel(c.sellReason)} · {formatDateTime(c.closedAt)}
                </div>
              </Td>
              <Td align="right" numeric>
                <div className={changeColorClass(c.realizedPnl)}>{usd ? signedPx(c.realizedPnl) : signedWon(c.realizedPnl)}</div>
                <div className="text-xs text-fg-subtle">{c.returnPercent === null ? '-' : formatPercent(c.returnPercent)}</div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <p className="mt-2 text-xs text-fg-subtle">
        {usd
          ? '손익은 앞서 나눠 판 익절 단까지 합친 달러 순손익입니다(수수료 뺌 · 미국은 거래세 없음). 환율 몫까지 넣은 원화 손익은 위 계좌 카드에 있습니다.'
          : '손익은 앞서 나눠 판 익절 단까지 합친 순손익입니다(수수료 · 거래세 뺌).'}
      </p>
    </Card>
  );
}

function ReviewsCard({ status }: { status: SwingStatus }) {
  if (status.reviews.length === 0) {
    return null;
  }
  return (
    <Card title="AI 회고">
      <ul className="space-y-3">
        {status.reviews.map((r) => (
          <li key={r.positionId} className="text-sm">
            <div className="font-medium text-fg">
              {r.stockName} · {swingReasonLabel(r.sellReason)}
              {r.returnPercent !== null && <span className="ml-1 text-fg-muted">({formatPercent(r.returnPercent)})</span>}
            </div>
            <p className="mt-0.5 whitespace-pre-line leading-relaxed text-fg-muted">{r.content}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** 상태 표시 — 판정은 swingBuyState(엔진 기록을 따른다) */
function StateBadge({ state, target, config }: { state: SwingBuyState; target: SwingTarget; config: SwingConfig }) {
  switch (state) {
    case 'held':
      return <Badge tone="info">보유 중</Badge>;
    case 'buy':
      return <Badge tone="success">매수 구간</Badge>;
    case 'blocked':
      // 근거: swingBuyState 가 skipReason !== null 일 때만 'blocked' 를 낸다
      return <Badge tone="warning">못 삼 · {skipReasonLabel(target.skipReason as NonNullable<SwingTarget['skipReason']>, config)}</Badge>;
    case 'watch':
      return null;
  }
}

function TargetsCard({ status, account }: { status: SwingStatus; account: SwingAccountKey }) {
  const [sector, setSector] = useState<string>(ALL_SECTORS);
  const [order, setOrder] = useState<'rank' | 'buy'>('rank');
  /*
    🔎 열린 AI 2차 검토 국면 — 스윙(6개월)만 부른다(2026-10-18 사용자 결정)
      저점 신호 검토 화면과 같은 기간(AI_REVIEW_DAYS)이라 queryKey 가 같아 캐시를 나눠 쓴다.
      🔴 감시 표는 이 응답을 기다리지 않는다 — 로딩 · 실패 · 빈 목록이면 칩 없이 지금과 같은 표다
  */
  const aiReview = useAiReview(AI_REVIEW_DAYS, isAiReviewAccount(account));
  // 🔴 꺼 둔 쿼리도 캐시에 남은 값은 돌려준다 — 그래서 계좌로 한 번 더 거른다(스윙(3개월) 감시 표에 칩이 붙지 않게)
  const openEpisodes = openEpisodesBySymbol(isAiReviewAccount(account) ? (aiReview.data?.episodes ?? []) : []);
  const heldIds = new Set(status.positions.map((p) => p.stockId));
  const { px } = bandPriceFormat(status.config.currency);
  const country = status.config.currency === 'USD' ? 'US' : 'KR';
  // 살아 있는 시세는 쓰지 않는다 — 이 탭은 2분마다 응답 전체를 다시 받는다(현재가 · 거리가 같은 순간 값)
  const noLive = () => null;

  const filtered = filterBySector(status.targets, sector);
  const rows = order === 'buy' ? sortByBuyOrder(filtered, noLive, heldIds) : sortByRank(filtered);
  const header = sector === ALL_SECTORS ? null : sectorHoldingLabel(status.sectors, sector);

  return (
    <Card title={`감시 대상 ${status.targets.length}종목${status.baseDate ? ` · ${status.baseDate} 기준` : ''}`}>
      {/* 섹터 필터 — "모두" 가 기본(사용자 10/10) · 보기 전환과 함께 걸린다 */}
      <div className="mb-2 flex flex-wrap gap-1" role="tablist" aria-label="섹터 거르기">
        {[ALL_SECTORS, ...status.sectors.map((s) => s.name)].map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={sector === name}
            onClick={() => setSector(name)}
            className={cx(
              'rounded-full px-3 py-1 text-xs',
              sector === name ? 'bg-brand text-brand-fg' : 'bg-surface-hover text-fg-muted',
            )}
          >
            {name === ALL_SECTORS ? '모두' : name}
          </button>
        ))}
      </div>
      <div className="mb-2 flex flex-wrap items-center gap-1" role="tablist" aria-label="감시 대상 줄 세우기">
        {(
          [
            ['rank', '왕복성 순위'],
            ['buy', '매수 순서(예상)'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={order === key}
            onClick={() => setOrder(key)}
            className={cx(
              'rounded-full px-3 py-1 text-xs',
              order === key ? 'bg-brand text-brand-fg' : 'bg-surface-hover text-fg-muted',
            )}
          >
            {label}
          </button>
        ))}
        {header !== null && <span className="ml-auto text-xs font-medium text-fg">{header}</span>}
      </div>
      {order === 'buy' && (
        <p className="mb-2 text-xs text-fg-subtle">
          1분마다 감시해서 먼저 진입선에 닿은 종목부터 삽니다. 같은 분에 여러 종목이 닿으면 왕복성 순위가 높은 것부터 삽니다.
          섹터마다 {status.config.sectorHold}종목 · 전체 {status.config.maxPositions}종목이 차거나 예수금이 모자라면 뒤는 못 삽니다.
        </p>
      )}
      {rows.length === 0 ? (
        <p className="text-sm text-fg-muted">이 섹터에 감시 대상이 없습니다.</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{order === 'buy' ? '종목 (앞: 순위)' : '종목'}</Th>
              <Th align="right">현재가</Th>
              <Th align="right">진입선 · 거리</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const state = swingBuyState(t, null, heldIds.has(t.stockId));
              const episode = openEpisodes.get(t.symbol);
              return (
                <tr key={t.stockId}>
                  <Td>
                    <span className="mr-1 text-xs text-fg-subtle">{t.swingRank}</span>
                    <StockLink stockId={t.stockId} country={country} className="font-medium text-fg">
                      {t.stockName}
                    </StockLink>
                    {/* 🔎 열린 국면 칩 — 누르면 저점 신호 검토의 그 국면 카드로(펼쳐서) */}
                    {episode && (
                      <Link
                        to={aiReviewHref(episode.id)}
                        title={`${AI_REVIEW_MARK_LABEL} — 국면 ${episode.phaseDay}일째 · 보고서 보기`}
                        aria-label={`${AI_REVIEW_MARK_LABEL} — 국면 ${episode.phaseDay}일째 · 보고서 보기`}
                        className="ml-1 inline-flex items-center whitespace-nowrap rounded-full border border-line px-1.5 py-0.5 align-middle text-[11px] leading-none text-fg-muted hover:text-fg"
                      >
                        {aiReviewChipText(episode)}
                      </Link>
                    )}
                    <div className="text-xs text-fg-subtle">{t.sector}</div>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      <StateBadge state={state} target={t} config={status.config} />
                      {t.reentry && <Badge tone="neutral">재진입 대기</Badge>}
                    </div>
                  </Td>
                  <Td align="right" numeric>
                    {t.currentPrice === null ? '-' : px(t.currentPrice)}
                  </Td>
                  <Td align="right" numeric>
                    <div>{px(t.bandPrice)}</div>
                    <div className={cx('text-xs', t.distancePercent !== null && t.distancePercent <= 0 ? 'text-fg' : 'text-fg-subtle')}>
                      {t.distancePercent === null ? '-' : formatPercent(t.distancePercent)}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      <p className="mt-2 text-xs text-fg-subtle">
        "못 삼" 이유는 매매 엔진이 오늘 실제로 남긴 기록입니다. 재진입 대기 종목은 손절 뒤라 진입선이 기준가({status.config.window}거래일 범위
        하위 {status.config.buyPercentile}%)보다 낮게 잡혀 있습니다.
      </p>
    </Card>
  );
}
